









































import Vue from 'vue';
import SkillProvider from "@/api/SkillProvider";
import ResponsiveCardList from "@/components/util/ResponsiveCardList.vue";
import ResponsiveCardListEntry from "@/components/util/ResponsiveCardListEntry.vue";
import SkillStubLink  from "@/components/skill/SkillStubLink.vue";
import DvBrowseFrame from "@/components/util/DvBrowseFrame.vue";
import DvStagger from "@/components/util/DvStagger.vue";
import ISkill from '../../models/skills/ISkill';

//  Mirrors DvBrowseFrame.vue's own (unimported) BrowseState -- every other
//  .vue-to-.vue import in this codebase pulls only the default export, so
//  this small shape is duplicated locally rather than betting on named-export
//  resolution through the *.vue ambient shim. See ItemSearchEx.vue.
type BrowseState = 'prompt' | 'loading' | 'results' | 'empty' | 'error' | 'end';

//  Skill search accepts either a name or a raw skill id, so a single
//  character is a legitimate query -- unlike item search, whose 1-char
//  cliff (97k rows / 74.8 MB / 20.3s) is specific to the item table.
//  Measured empirically against the live `duck` region: a 1-char skill
//  name search ('c') returned in well under a second with a small payload.
//  See task-8-9-report.md for the exact numbers.
const MIN_LENGTH = 1;

interface IData {
    query: string;
    loading: boolean;
    error: string;
    skills: ISkill[];
    //  Guards against a stale response clobbering a newer one: the frame
    //  debounces input, but a fast retype (or the initialQuery watcher firing
    //  mid-flight) can still leave two requests in the air at once.
    loadSeq: number;
}

export default Vue.extend({
    components: {
        DvBrowseFrame,
        DvStagger,
        ResponsiveCardList,
        ResponsiveCardListEntry,
        SkillStubLink,
    },
    props: {
        "initialQuery": {
            type: String as () => string,
        },
    },
    data(): IData {
        return {
            query: this.initialQuery || '',
            loading: false,
            error: '',
            skills: [],
            loadSeq: 0,
        };
    },
    watch: {
        //  Identity-guarded: search() itself emits `query` after every
        //  search, Skills.vue routes that into initialQuery, and this watcher
        //  firing unconditionally on that round trip re-searches a value the
        //  component already holds -- a second, redundant request per query.
        initialQuery() {
            if (this.initialQuery === this.query) {
                return;
            }
            this.query = this.initialQuery;
            this.search();
        }
    },
    computed: {
        minLength(): number {
            return MIN_LENGTH;
        },
        hasQuery(): boolean {
            return this.query.trim().length >= MIN_LENGTH;
        },
        state(): BrowseState {
            if (!this.hasQuery) {
                return 'prompt';
            }
            if (this.loading) {
                return 'loading';
            }
            if (this.error) {
                return 'error';
            }
            return this.skills.length === 0 ? 'empty' : 'results';
        },
    },
    mounted() {
        this.search();
    },
    methods: {
        onInput(value: string) {
            this.query = value;
            this.search();
        },
        onRetry() {
            this.search();
        },
        async search() {
            if (!this.hasQuery) {
                this.loadSeq++;
                this.skills = [];
                this.error = '';
                this.loading = false;
                return;
            }

            const seq = ++this.loadSeq;
            this.loading = true;
            this.error = '';

            try {
                const trimmed = this.query.trim();
                const asN = Number(trimmed);
                let results: ISkill[];
                //  Digits-only rather than Number()/isNaN(): the latter also
                //  accepts "12.5", "-5" and "1e5", and there is no such thing
                //  as a decimal or negative id in this data — so those should
                //  fall through to a name search rather than a doomed point
                //  lookup. Matches ItemSearchEx and QuickNav.
                if (/^\d+$/.test(trimmed)) {
                    //  getSkill REJECTS on a missing id (unlike
                    //  ItemsProvider.getStub, which resolves null), so a 404
                    //  here means "no skill with that id" — an empty result,
                    //  not a failed search. Swallow only that; a 500, a
                    //  network drop or anything else is a real error and must
                    //  still reach the error state.
                    let sk: ISkill | null = null;
                    try {
                        sk = await SkillProvider.getSkill(asN);
                    } catch (e) {
                        if ((e as any)?.response?.status !== 404) {
                            throw e;
                        }
                    }
                    results = sk ? [sk] : [];
                } else {
                    results = await SkillProvider.searchSkillsByName(this.query);
                }
                if (seq !== this.loadSeq) {
                    return;
                }
                this.skills = results;
            } catch (e) {
                if (seq !== this.loadSeq) {
                    return;
                }
                this.error = String(e);
                this.skills = [];
            } finally {
                if (seq === this.loadSeq) {
                    this.loading = false;
                }
            }

            if (seq === this.loadSeq) {
                this.$emit("query", this.query);
            }
        }
    }
});
